import {FC, useEffect, useRef} from 'react'
import classNames from 'classnames'
import {Link, matchPath} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {usePolicy} from 'hooks/usePolicy'
import {useToggle} from 'hooks/useToggle'
import {
  AnalyticsRouteRestriction,
  useAnalyticsRouteRestriction,
  useAnalyticsTenantDomainGate,
} from 'features/Analytics/hooks'
import {NoStorageRouteTooltip} from 'features/Analytics/components/NoStorageRouteTooltip'
import {stringSplitAndJoin} from 'utils'
import {ChevronRightIcon} from '../SvgIcon'
import {TooltipBody, TooltipWrapper} from '../TooltipWrapper'
import {AnalyticsRoutesEnum} from 'interfaces'

const routes = [
  {name: 'Overview', path: AnalyticsRoutesEnum.ANALYTICS_OVERVIEW},
  {name: 'Policy', path: AnalyticsRoutesEnum.ANALYTICS_POLICY},
  {name: 'Productivity', path: AnalyticsRoutesEnum.ANALYTICS_PRODUCTIVITY},
  {name: 'Shadow IT', path: AnalyticsRoutesEnum.ANALYTICS_SHADOW_IT},
  {name: 'AI Usage', path: AnalyticsRoutesEnum.ANALYTICS_AI_USAGE_APPS},
  {name: 'Detail', path: AnalyticsRoutesEnum.ANALYTICS_DETAIL},
]

const ANALYTICS_DROPDOWN_TENANT_TOOLTIP_ID =
  'analytics-route-dropdown-tenant-tooltip'

const isDropdownItemActive = (
  itemPath: string,
  currentPath: string,
): boolean => {
  if (itemPath === currentPath) {
    return true
  }

  if (itemPath === AnalyticsRoutesEnum.ANALYTICS_AI_USAGE_APPS) {
    return Boolean(
      matchPath(currentPath, {
        path: AnalyticsRoutesEnum.ANALYTICS_AI_USAGE,
        exact: false,
      }),
    )
  }

  return false
}

interface Props {
  bgColor: string
  textColor: string
  text: string
  bgOpacity: string
  bgHoverOpacity: string
  className?: string
  menuClassName?: string
}

export const AnalyticsRouteDropDown: FC<Props> = ({
  bgColor,
  text,
  textColor,
  bgOpacity,
  bgHoverOpacity,
  className,
  menuClassName,
}) => {
  const {t} = useTranslation(['common'])
  const {pathname} = usePolicy()
  const {toggleOn, onToggleClick} = useToggle()
  const ref = useRef<HTMLDivElement>(null)
  const {getRouteRestriction} = useAnalyticsRouteRestriction()
  const {isAnalyticsDisabledForPrimaryDomain} = useAnalyticsTenantDomainGate()
  useEffect(() => {
    // to determine clicked target and close the dropdown
    const checkIfClickedOutside = (event: MouseEvent) => {
      if (
        toggleOn &&
        ref.current &&
        !ref.current.contains(event.target as Node)
      ) {
        onToggleClick()
      }
    }

    // adding event listener to know whenever user clicks on page
    document.addEventListener('mousedown', checkIfClickedOutside)

    // cleanup function to remove event listener on unmount
    return () => {
      document.removeEventListener('mousedown', checkIfClickedOutside)
    }
  }, [toggleOn])

  return (
    <div className="relative" ref={ref}>
      <button
        data-cy="analytics-dropdown"
        onClick={onToggleClick}
        aria-expanded={toggleOn}
        tabIndex={0}
        className={classNames(
          // Combine all the dynamic classes as a single string rather than multiple arguments
          `bg-${bgColor} bg-${bgOpacity} hover:bg-${bgHoverOpacity} border-${bgColor}`,
          // Fixed classes
          'outline-none rounded-full flex items-center justify-center border focus-visible:border-white px-4 py-1 ml-3',
          // Phones: the compact 34px pill, 44px tap area, with press feel.
          "mobile:relative mobile:min-h-[34px] mobile:px-3 mobile:after:absolute mobile:after:-inset-y-[5px] mobile:after:inset-x-0 mobile:after:content-[''] mobile:transition-transform mobile:duration-100 mobile:active:scale-95",
          // Separate argument for the custom className prop
          className,
        )}
      >
        <span
          className={classNames(
            'text-sm karla-medium mobile:whitespace-nowrap mobile:text-13px',
            textColor,
          )}
        >
          {text}
        </span>
        <ChevronRightIcon
          strokeColor={textColor}
          classes={classNames('transform rotate-90 ml-4 mobile:ml-2.5', {
            '-rotate-90': toggleOn,
          })}
          width={6}
          height={9}
        />
      </button>

      <div
        className={classNames(
          'absolute z-50 bg-dope-black border border-gray-600 flex flex-col w-40 mt-3 mx-auto mobile:right-0',
          menuClassName,
          {
            hidden: toggleOn === false,
          },
        )}
      >
        <NoStorageRouteTooltip place="left-start">
          {getNoStorageTriggerProps =>
            routes.map((data, idx) => {
              const restriction = getRouteRestriction(data.path)
              const key = `analytics-route-item-${idx}`
              const dataCy = `analytics-dropdown-option-${stringSplitAndJoin(
                data?.name?.toLowerCase(),
              )}`
              const dividerClasses = {
                'border-b border-gray-700': idx !== routes.length - 1,
              }
              const restrictedItemClasses = classNames(
                'w-full py-2 karla-light text-sm text-white/40 mobile:min-h-[44px]',
                dividerClasses,
              )

              if (restriction === AnalyticsRouteRestriction.NoStorage) {
                return (
                  <button
                    key={key}
                    {...getNoStorageTriggerProps(data.name)}
                    type="button"
                    data-cy={dataCy}
                    className={classNames(
                      restrictedItemClasses,
                      'cursor-pointer',
                    )}
                  >
                    {data.name}
                  </button>
                )
              }

              if (restriction) {
                return (
                  <button
                    key={key}
                    type="button"
                    data-cy={dataCy}
                    {...(restriction ===
                      AnalyticsRouteRestriction.TenantUnavailable && {
                      'data-tooltip-content': t(
                        'ANALYTICS_TENANT_UNAVAILABLE_TOOLTIP',
                      ),
                      'data-tooltip-id': ANALYTICS_DROPDOWN_TENANT_TOOLTIP_ID,
                    })}
                    disabled
                    className={classNames(
                      restrictedItemClasses,
                      'cursor-not-allowed',
                    )}
                  >
                    {data.name}
                  </button>
                )
              }

              return (
                <Link
                  className={classNames(
                    'block w-full py-2 text-center karla-light text-sm hover:bg-gray-800 mobile:flex mobile:min-h-[44px] mobile:items-center mobile:justify-center mobile:active:bg-gray-800',
                    dividerClasses,
                    {[textColor]: isDropdownItemActive(data.path, pathname)},
                  )}
                  data-cy={dataCy}
                  to={data.path}
                  key={key}
                >
                  {data.name}
                </Link>
              )
            })
          }
        </NoStorageRouteTooltip>
      </div>
      {isAnalyticsDisabledForPrimaryDomain ? (
        <TooltipWrapper
          id={ANALYTICS_DROPDOWN_TENANT_TOOLTIP_ID}
          place="bottom"
          className="z-tooltip"
          render={({content}) => (
            <TooltipBody classes="shadow-none" noPadding>
              {content}
            </TooltipBody>
          )}
        />
      ) : null}
    </div>
  )
}
